"use client"

import { useState, useEffect, useCallback } from "react"
import { Header } from "@/templates/Admin/components/Manager/header"
import { SearchToolbar } from "@/templates/Admin/components/Manager/SearchToolbar"
import { FilterSidebar } from "@/templates/Admin/components/Manager/FilterSidebar"
import { ResultsControls } from "@/templates/Admin/components/Manager/ResultsControls"
import { SearchResults } from "@/templates/Admin/components/Manager/SearchResults"
import { BorrowRequestFooter, type BorrowRequestData } from "@/templates/Admin/components/Manager/BorrowRequestFooter"
import { BorrowSuccessModal } from "@/templates/Admin/components/Manager/BorrowSuccessModal"
import type { Document as DocumentType, CartItem } from "@/types"
import LayoutDefault from "../../layouts/LayoutDefault"
import GioMuonPage from "./cart"
import LibraryDocumentPage from "./libraryDocumentPage"
import { useIdPath } from "@/contexts/IdPathProvider"
import assetServices from "../../utils/services/asset"
import { cartService } from "@/services/cart.service"
import { assetLoanService } from "@/services/assetLoan.service"
import { AddToCartPayload } from "@/types/cart.types"
import { useSession } from 'next-auth/react'
import { useToast } from "@/templates/Admin/components/Toast"

// Props interface
interface ManagerProps {
  isOpen?: boolean
  onClose?: () => void
  isSidebarOpen?: boolean
  isEmbedded?: boolean
}

export default function Manager({ isOpen = true, onClose = () => { }, isSidebarOpen = true, isEmbedded }: ManagerProps) {  // State quản lý UI
  const [showFilters, setShowFilters] = useState(false)
  const [showRequestFooter, setShowRequestFooter] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showCartPage, setShowCartPage] = useState(false)
  const [showLibraryDocumentPage, setShowLibraryDocumentPage] = useState(false)
  const [selectedDocForLibrary, setSelectedDocForLibrary] = useState<DocumentType | null>(null)
  const [currentBorrowRequestId, setCurrentBorrowRequestId] = useState("")
  const [currentLoanId, setCurrentLoanId] = useState(0)

  // State quản lý dữ liệu
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [cartItems, setCartItems] = useState<CartItem[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [documentType, setDocumentType] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState("relevance")
  const [selectedFilters, setSelectedFilters] = useState<string[]>([])

  const { idPath, namePath } = useIdPath();
  const { data: session } = useSession();
  const { showSuccess, showError } = useToast();
  const [realDocuments, setRealDocuments] = useState<DocumentType[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loadingDocs, setLoadingDocs] = useState(true);

  // Get user-specific localStorage key
  const getCartStorageKey = useCallback(() => {
    const userId = (session as any)?.userData?.id || (session as any)?.userData?.user_id || (session?.user as any)?.id || (session?.user as any)?.user_id;
    return userId ? `borrowCart_${userId}` : 'borrowCart';
  }, [session]);

  // Fetch real assets from API
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        if (!idPath) return;
        setLoadingDocs(true);
        const params: any = {
          id_path: idPath,
          page: currentPage,
          limit: 10,
        };
        if (searchQuery) params.search = searchQuery;
        if (documentType && documentType !== 'all') params.type = documentType;
        
        if (sortBy && sortBy !== 'relevance') params.sortBy = sortBy;
        const filterTypes = selectedFilters
          .filter(f => f.startsWith("Loại:"))
          .map(f => f.replace("Loại:", "").toLowerCase());
        if (filterTypes.length > 0) {
          params.types = filterTypes.join(',');
        }
        const filterStatus = selectedFilters
          .filter(f => f.startsWith("Trạng thái:"))
          .map(f => {
            if (f.includes("Trong kho lưu trữ")) return "available";
            if (f.includes("Đang được mượn")) return "borrowed";
            return "";
          }).filter(Boolean);
        if (filterStatus.length > 0) {
          params.statuses = filterStatus.join(',');
        }

        const response = await assetServices.getAllAssets(params) as any;
        if (response && response.success && Array.isArray(response.data)) {
          setTotalResults(response.pagination?.totalItems || response.data.length);
          // Lọc bỏ writing — chỉ giữ asset có type thực (document, photo, video, model...)
          const filtered = response.data.filter((a: any) =>
            (a.asset_type_name || '').toLowerCase() !== 'writing'
          );
          const mappedDocs = filtered.map((asset: any) => {
            const attrs = asset.attributes || {};
            const title = attrs['title_vn']?.values
              || attrs['title_en']?.values
              || attrs['ten_goi']?.values
              || attrs['title']?.values
              || attrs['thumbnail']?.title
              || attrs['video']?.title
              || attrs['document']?.title
              || asset.name
              || asset.asset_name
              || `${asset.asset_type_name || 'Hiện vật'} #${asset.uuid?.slice(-6) || ''}`;

            const coverImage = attrs['thumbnail']?.values
              || attrs['imgage']?.values
              || attrs['image']?.values
              || '/images/placeholder-664.png';

            // Ẩn badge "writing" — không hiện trên giao diện
            const rawType = (asset.asset_type_name || '').toLowerCase();
            const displayType = rawType === 'writing' ? '' : (asset.asset_type_name || '');
            const isBorrowed = ['borrowed', 'overdue', 'draft', 'pending', 'approved'].includes(asset.borrow_status || '');

            return {
              id: asset.uuid,
              title,
              authors: asset.created_by_name ? [asset.created_by_name] : [],
              year: asset.created_at ? new Date(asset.created_at).getFullYear() : null,
              type: displayType,
              subjects: [asset.category_name || asset.category_title].filter(Boolean),
              language: 'Tiếng Việt',
              description: asset.description || attrs['description']?.values || attrs['mo_ta']?.values || '',
              available: !isBorrowed,
              waitlist: 0,
              location: isBorrowed ? 'Đang mượn' : 'Trong kho',
              borrowStatus: asset.borrow_status,
              coverImage,
              views: asset.view_count || 0,
            };
          });
          setRealDocuments(mappedDocs);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingDocs(false);
      }
    };
    fetchAssets();
  }, [idPath, currentPage, searchQuery, documentType, sortBy, selectedFilters]);

  // Load giỏ mượn từ API khi component mount
  useEffect(() => {
    const loadCartFromAPI = async () => {
      if (!idPath) return;
      try {
        const items = await cartService.getCartItems(idPath);
        if (Array.isArray(items)) {
          // Transform API items to CartItem format for UI compatibility
          // Include cartId for potential remove operations
          const transformedItems: any[] = items.map((item: any) => ({
            id: item.asset_id, // Use asset_id as id for UI
            cartId: item.uuid || item.id || item.asset_id, // Store the actual cart ID for removal
            asset_id: item.asset_id,
            title: item.name || `Asset ${item.asset_id?.slice(-6) || ''}`,
            type: item.type || '',
            coverImage: item.thumbnail,
            requestedQuantity: item.quantity,
            borrowDate: item.borrow_date,
            returnDate: item.return_date,
            available: true,
            waitlist: 0,
          }));
          setCartItems(transformedItems);
        }
      } catch (e) {
        console.error("Failed to load cart from API:", e);
        // Fallback to localStorage
        try {
          const savedCart = localStorage.getItem(getCartStorageKey());
          if (savedCart) {
            const parsed = JSON.parse(savedCart);
            setCartItems(Array.isArray(parsed) ? parsed : []);
          }
        } catch (storageErr) {
          console.warn("Failed to parse saved cart:", storageErr);
        }
      }
    };
    loadCartFromAPI();
  }, [idPath]);

  // Cập nhật giỏ mượn
  const updateCart = (newCartItems: CartItem[]) => {
    setCartItems(newCartItems);
    try {
      localStorage.setItem(getCartStorageKey(), JSON.stringify(newCartItems));
    } catch (storageErr) {
      console.warn("Failed to save cart to localStorage:", storageErr);
    }
    // Dispatch event để cập nhật header
    window.dispatchEvent(new Event("cartUpdated"));
  };

  // Thêm tài liệu vào giỏ mượn (API-based)
  const addToCart = async (document?: DocumentType) => {
    if (!document || !document.id) {
      showError("Không thể thêm tài liệu vào giỏ: thiếu ID");
      return;
    }
    if (!idPath) {
      showError("Không tìm thấy thông tin dự án");
      return;
    }

    try {
      // Always check API cart for duplicates (not local state which may be stale)
      const apiItems = await cartService.getCartItems(idPath);
      const safeApiItems = Array.isArray(apiItems) ? apiItems : [];
      const existsInAPI = safeApiItems.some((item: any) => item.asset_id === document.id);

      if (existsInAPI) {
        showError("Tài liệu này đã có trong giỏ mượn!");
        // Sync local state with API
        const transformedItems: any[] = safeApiItems.map((item: any) => ({
          id: item.asset_id,
          cartId: item.uuid || item.id || item.asset_id,
          asset_id: item.asset_id,
          title: item.name || `Asset ${item.asset_id?.slice(-6) || ''}`,
          type: item.type || '',
          coverImage: item.thumbnail,
          requestedQuantity: item.quantity,
          borrowDate: item.borrow_date,
          returnDate: item.return_date,
          available: true,
          waitlist: 0,
        }));
        setCartItems(transformedItems);
        try {
          localStorage.setItem(getCartStorageKey(), JSON.stringify(transformedItems));
        } catch (err) {}
        return;
      }

      // Add to API
      const payload: AddToCartPayload = {
        asset_id: document.id,
        quantity: 1,
        borrow_date: getTodayDate(),
        return_date: getDefaultReturnDate(),
        request_purpose: 'cho-muon',
      };
      await cartService.addToCart(idPath, payload);

      // Refresh cart from API
      const refreshedItems = await cartService.getCartItems(idPath);
      const safeRefreshedItems = Array.isArray(refreshedItems) ? refreshedItems : [];
      const transformedItems: any[] = safeRefreshedItems.map((item: any) => ({
        id: item.asset_id,
        cartId: item.uuid || item.id || item.asset_id,
        asset_id: item.asset_id,
        title: item.name || `Asset ${item.asset_id?.slice(-6) || ''}`,
        type: item.type || '',
        coverImage: item.thumbnail,
        requestedQuantity: item.quantity,
        borrowDate: item.borrow_date,
        returnDate: item.return_date,
        available: true,
        waitlist: 0,
      }));
      setCartItems(transformedItems);
      try {
        localStorage.setItem(getCartStorageKey(), JSON.stringify(transformedItems));
      } catch (err) {}
      showSuccess("Đã thêm vào giỏ mượn!");
    } catch (e: any) {
      console.error("Failed to add to cart:", e);
      const errorMsg = e?.response?.data?.message || "Không thể thêm tài liệu vào giỏ mượn";
      showError(errorMsg);
    }
  };
  // Xử lý submit phiếu mượn (API-based)
  const handleSubmitRequest = async (requestData: BorrowRequestData) => {
    if (!idPath) {
      showError("Không tìm thấy thông tin dự án");
      return;
    }

    try {
      console.log("Tạo phiếu mượn:", requestData);



      const borrowDate = requestData.useSameDates && requestData.globalBorrowDate
        ? requestData.globalBorrowDate
        : (requestData.items[0]?.borrowDate || getTodayDate());
      const returnDate = requestData.useSameDates && requestData.globalReturnDate
        ? requestData.globalReturnDate
        : (requestData.items[0]?.returnDate || getDefaultReturnDate());

      // Get user info from session
      const userName = (session?.user as any)?.name || (session?.user as any)?.full_name || (session?.user as any)?.email || 'Người mượn';
      const userId = (session?.user as any)?.user_id || (session?.user as any)?.id || 0;

      // Create loan request via API
      const result = await assetLoanService.createLoanRequest(idPath, {
        borrower_id: userId,
        borrower_name: userName,
        borrower_department: '',
        borrower_phone: '',
        borrower_email: (session?.user as any)?.email || '',
        loan_purpose: requestData.reason || `Yêu cầu ${requestData.requestType}`,
        loan_type: requestData.requestType as any,
        expected_borrow_date: borrowDate,
        expected_return_date: returnDate,
        priority: 'normal',
        notes: requestData.reason || '',
        approval_mode: requestData.approval_mode,
        approvers: requestData.approvers,
      });

      const loanId = result.loan_id;
      const loanCode = result.loan_code || generateBorrowRequestId();
      setCurrentBorrowRequestId(loanCode);
      setCurrentLoanId(loanId);

      // Add items to loan via API
      for (const item of requestData.items) {
        try {
          await assetLoanService.addLoanItem(idPath, loanId, {
            asset_uuid: item.id,
            asset_name: item.title,
            asset_type: item.type || '',
            quantity_requested: item.requestedQuantity || 1,
          });
        } catch (itemErr) {
          console.error(`Lỗi thêm item ${item.title}:`, itemErr);
        }
      }

      // Remove items from cart via API (clear entire cart after loan creation)
      try {
        await cartService.clearCart(idPath);
      } catch (cartErr) {
        console.error(`Lỗi xóa giỏ:`, cartErr);
      }

      // Refresh cart from API (should be empty now)
      const items = await cartService.getCartItems(idPath);
      const transformedItems: CartItem[] = (items || []).map((item: any) => ({
        id: item.asset_id,
        cartId: item.uuid || item.id || item.asset_id,
        asset_id: item.asset_id,
        title: item.name || `Asset ${item.asset_id?.slice(-6) || ''}`,
        type: item.type || '',
        coverImage: item.thumbnail,
        requestedQuantity: item.quantity,
        borrowDate: item.borrow_date,
        returnDate: item.return_date,
        available: true,
        waitlist: 0,
        authors: [],
        year: new Date().getFullYear(),
        description: '',
        subjects: [],
        location: 'Trong kho',
        language: 'Tiếng Việt',
      }));
      setCartItems(transformedItems);
      localStorage.setItem(getCartStorageKey(), JSON.stringify(transformedItems));

      setSelectedItems([]);
      setShowRequestFooter(false);
      setShowSuccessModal(true);
    } catch (err: any) {
      console.error("Lỗi tạo phiếu mượn:", err);
      showError(`Lỗi tạo phiếu mượn: ${err?.message || 'Vui lòng thử lại'}`);
    }
  }

  // Utility functions
  const getTodayDate = () => {
    const today = new Date()
    return today.toISOString().split("T")[0]
  }

  const getDefaultReturnDate = () => {
    const today = new Date()
    today.setDate(today.getDate() + 14)
    return today.toISOString().split("T")[0]
  }
  const generateBorrowRequestId = () => {
    const randomBytes = new Uint8Array(8);
    crypto.getRandomValues(randomBytes);
    const randomHex = Array.from(randomBytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `ID-${Date.now()}-${randomHex}`;
  }

  const getSelectedCartItems = () => {
    return cartItems.filter((item) => selectedItems.includes(item.id))
  }

  // Handle showing library document page
  const handleViewOnlineDocument = (document?: DocumentType) => {
    if (document) {
      setSelectedDocForLibrary(document);
    }
    setShowLibraryDocumentPage(true);
  };
  // Always include base types + any additional from actual data
  const baseTypes = ['photo', 'video', 'model'];
  const dataTypes = realDocuments.map(doc => doc.type?.toLowerCase()).filter(Boolean);
  const uniqueTypes = Array.from(new Set([...baseTypes, ...dataTypes])).filter(Boolean);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, documentType, sortBy, selectedFilters]);

  // Using isOpen prop directly
  if (!isOpen) return null;

  // Show cart page if requested
  if (showCartPage) {
    return (
      <LayoutDefault onClose={onClose} isSidebarOpen={isSidebarOpen} isEmbedded={isEmbedded}>
        <GioMuonPage
          isOpen={true}
          onClose={() => setShowCartPage(false)}
          isSidebarOpen={isSidebarOpen}
        />
      </LayoutDefault>
    )
  }

  // Show library document page if requested
  if (showLibraryDocumentPage && selectedDocForLibrary) {
    return (
      <LayoutDefault onClose={onClose} isSidebarOpen={isSidebarOpen} isEmbedded={isEmbedded}>
        <LibraryDocumentPage
          document={selectedDocForLibrary}
          onExit={() => {
            setShowLibraryDocumentPage(false)
            setSelectedDocForLibrary(null)
          }}
          onGoToCart={() => {
            setShowLibraryDocumentPage(false)
            setSelectedDocForLibrary(null)
            setShowCartPage(true)
          }} onAddToCart={() => {
            addToCart(selectedDocForLibrary)
          }}
          onCreateBorrowRequest={() => {
            // Add to cart first, then create borrow request
            addToCart(selectedDocForLibrary)

            // Create quick borrow request data
            const requestData: BorrowRequestData = {
              requestType: "borrow",
              reason: "Tạo phiếu mượn nhanh từ trang chi tiết tài liệu",
              useSameDates: true,
              globalBorrowDate: getTodayDate(),
              globalReturnDate: getDefaultReturnDate(),
              approval_mode: "single",
              approvers: [],
              items: [{
                ...selectedDocForLibrary,
                id: selectedDocForLibrary.id || '',
                authors: selectedDocForLibrary.authors || [],
                year: selectedDocForLibrary.year || new Date().getFullYear(),
                description: selectedDocForLibrary.description || '',
                subjects: selectedDocForLibrary.subjects || [],
                location: selectedDocForLibrary.location || 'Trong kho',
                language: selectedDocForLibrary.language || 'Tiếng Việt',
                available: selectedDocForLibrary.available !== undefined ? selectedDocForLibrary.available : true,
                waitlist: selectedDocForLibrary.waitlist || 0,
                requestedQuantity: 1,
                borrowDate: getTodayDate(),
                returnDate: getDefaultReturnDate(),
                addedAt: new Date().toISOString(),
              } as CartItem]
            }

            handleSubmitRequest(requestData)
            setShowLibraryDocumentPage(false)
            setSelectedDocForLibrary(null)
          }}
        />
      </LayoutDefault>
    )
  }

  return (
    <LayoutDefault onClose={onClose} isSidebarOpen={isSidebarOpen} isEmbedded={isEmbedded}>
      <main className="bg-gray-50">      {/* Header - compact, sát trên */}
        <Header onCartClick={() => setShowCartPage(true)} onExit={onClose} />

        {/* Main Content - không padding thừa */}
        <div className="px-3 py-2">
          {/* Search Toolbar */}
          <SearchToolbar
            onToggleFilters={() => setShowFilters(!showFilters)}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            documentType={documentType}
            onDocumentTypeChange={setDocumentType}
            availableTypes={uniqueTypes}
          />

          {/* Content Area */}
          <div className="flex gap-3 mt-2">
            {/* Filter Sidebar */}
            <FilterSidebar 
              isOpen={showFilters} 
              onClose={() => setShowFilters(false)}
              sortBy={sortBy}
              setSortBy={setSortBy}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
            />
            {/* Main Results */}
            <div className={`transition-all duration-300 ${showFilters ? "lg:w-3/4 w-full" : "w-full"}`}>
              <ResultsControls
                selectedCount={selectedItems.length}
                onSelectAll={(checked) => {
                  if (checked) {
                    setSelectedItems([])
                  } else {
                    setSelectedItems([])
                  }
                }}
                currentPage={currentPage}
                totalResults={totalResults}
                itemsPerPage={10}
              />
              {loadingDocs ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  <span className="ml-3 text-gray-500">Đang tải dữ liệu hiện vật...</span>
                </div>
              ) : (
                <SearchResults
                  documents={realDocuments}
                  searchQuery={searchQuery}
                  documentType={documentType}
                  currentPage={currentPage}
                  totalResults={totalResults}
                  onPageChange={setCurrentPage}
                  selectedItems={selectedItems}
                  onToggleSelect={(itemId, checked) => {
                    if (checked) {
                      setSelectedItems([...selectedItems, itemId])
                    } else {
                      setSelectedItems(selectedItems.filter((id) => id !== itemId))
                    }
                  }}
                  onAddToCart={(doc: DocumentType) => addToCart(doc)}
                  onViewOnlineDocument={handleViewOnlineDocument}
                />
              )}
            </div>
          </div>        {/* Floating Action Button for Cart */}

        </div>

        {/* Footer Form for Borrow Request */}
        <BorrowRequestFooter
          isOpen={showRequestFooter}
          onClose={() => setShowRequestFooter(false)}
          selectedItems={getSelectedCartItems()}
          onSubmit={handleSubmitRequest}
        />      {/* Success Modal */}
        <BorrowSuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          borrowRequestId={currentBorrowRequestId}
          loanId={currentLoanId}
          namePath={namePath}
        />
      </main>
    </LayoutDefault>
  )
}
